import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Message, Placeholder } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';
import PageHeader from '../components/shared/PageHeader';
import SurveyForm from '../components/surveys/SurveyForm';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import { useMySurvey, useSaveMyResponse } from '../hooks/useSurveys';
import { ANONYMOUS_NOTE, formatDate, isAnswered, visibleBlockIds } from '../lib/surveys';
import { showToast } from '../utils/toast';
import './TakeSurveyPage.css';

const errorText = (err, fallback) => String(err?.message || err || fallback);

const formatDateTime = (iso) => (iso
  ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '');

/*
  An anonymous survey's answers are never saved on the server before they are sent (a draft there
  would sit next to the person's name), so answers in progress are kept in this browser instead.
*/
const localKey = (id) => `ec.survey.${id}.answers`;
const readLocal = (id) => {
  try {
    return JSON.parse(window.localStorage.getItem(localKey(id)) || '{}') || {};
  } catch {
    return {};
  }
};
const writeLocal = (id, answers) => {
  try {
    if (answers) window.localStorage.setItem(localKey(id), JSON.stringify(answers));
    else window.localStorage.removeItem(localKey(id));
  } catch {
    // Not kept between visits; the survey still works.
  }
};

/**
 * Taking a survey: answer, save for later, send. Answers can be changed until it closes, except on
 * an anonymous survey, where they are sent once and can't be seen or changed afterwards.
 */
export default function TakeSurveyPage() {
  const { surveyId } = useParams();
  const { data: survey, isLoading, error } = useMySurvey(surveyId);

  if (isLoading) {
    return (
      <div className="bdc-page tks">
        <Placeholder.Paragraph rows={8} active />
      </div>
    );
  }
  if (error || !survey) {
    return (
      <div className="bdc-page tks">
        <PageHeader crumbs={[{ label: 'Home', to: '/' }, { label: 'Surveys', to: '/surveys' }, { label: 'Survey' }]} title="Survey" />
        <Message type="error" showIcon>{errorText(error, 'That survey could not be loaded.')}</Message>
      </div>
    );
  }
  return <TakeSurvey key={survey.id} survey={survey} />;
}

function TakeSurvey({ survey }) {
  const navigate = useNavigate();
  const save = useSaveMyResponse();
  const anonymous = survey.anonymous === true;
  const submitted = survey.myStatus === 'submitted';
  const [answers, setAnswers] = useState(() => (anonymous ? (submitted ? {} : readLocal(survey.id)) : survey.answers || {}));
  const [errors, setErrors] = useState({});
  const [confirmSend, setConfirmSend] = useState(false);

  const closed = survey.state !== 'open';
  const savedAnswers = useMemo(() => JSON.stringify(survey.answers || {}), [survey.answers]);
  // Anonymous answers in progress keep themselves in this browser, so there's nothing to lose.
  const dirty = !closed && !anonymous && JSON.stringify(answers) !== savedAnswers;
  const guard = useUnsavedChangesGuard(dirty);

  useEffect(() => {
    if (anonymous && !submitted && !closed) writeLocal(survey.id, answers);
  }, [anonymous, submitted, closed, survey.id, answers]);

  // Answering a question clears its "needs an answer".
  const change = (next) => {
    setAnswers(next);
    if (Object.keys(errors).length) {
      setErrors((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => !isAnswered(next[id]))));
    }
  };

  // Required questions that show and are still blank, checked here before the server does.
  const missingNow = () => {
    const visible = visibleBlockIds(survey.content, answers);
    return survey.content
      .filter((b) => b.type === 'question' && b.required && visible.has(b.id) && !isAnswered(answers[b.id]))
      .map((b) => b.id);
  };

  const showMissing = (ids) => {
    setErrors(Object.fromEntries(ids.map((id) => [id, 'This question needs an answer.'])));
    document.getElementById(`svf-q-${ids[0]}-label`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const persist = async (submit) => {
    try {
      const result = await save.mutateAsync({ id: survey.id, answers, submit });
      setAnswers(result.answers || {});
      setErrors({});
      setConfirmSend(false);
      if (anonymous) writeLocal(survey.id, null);
      showToast('success', submit
        ? (anonymous ? 'Thanks, your answers were sent anonymously.' : submitted ? 'Your changes were sent.' : 'Thanks, your answers were sent.')
        : 'Saved. Come back to finish any time before it closes.');
      if (submit && !submitted) window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setConfirmSend(false);
      let missing = null;
      try {
        missing = JSON.parse(err?.responseText || '{}')?.missing;
      } catch {
        missing = null;
      }
      if (Array.isArray(missing) && missing.length) showMissing(missing);
      showToast('error', errorText(err, 'Unable to save your answers'));
    }
  };

  const send = () => {
    const missing = missingNow();
    if (missing.length) {
      showMissing(missing);
      showToast('error', `Answer the ${missing.length === 1 ? 'required question' : `${missing.length} required questions`} first.`);
      return;
    }
    if (submitted) persist(true);
    else setConfirmSend(true);
  };

  const questionTotal = survey.content.filter((b) => b.type === 'question').length;

  return (
    <div className="bdc-page tks">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Surveys', to: '/surveys' }, { label: survey.title }]}
        icon="bi-ui-checks"
        title={survey.title}
        onBack={() => guard.requestLeave('/surveys')}
      />

      <div className="tks-sheet">
        {anonymous ? (
          <div className="tks-anon" role="note">
            <i className="bi bi-incognito" aria-hidden="true" />
            <div>
              <strong>Anonymous survey</strong>
              <p>{ANONYMOUS_NOTE}</p>
              {!submitted && !closed ? (
                <p>Once you send your answers you can&rsquo;t change them. Answers in progress are kept only in this browser.</p>
              ) : null}
            </div>
          </div>
        ) : null}

        {anonymous && submitted ? (
          <Message type="success" showIcon>
            You sent your answers. Because they aren&rsquo;t linked to you, they can&rsquo;t be shown or changed.
          </Message>
        ) : closed ? (
          <Message type="info" showIcon>
            This survey has closed. {submitted ? `You sent your answers on ${formatDateTime(survey.submittedAt)}; they are below.` : "Here's what you had saved."}
          </Message>
        ) : submitted ? (
          <Message type="success" showIcon>
            You sent your answers on {formatDateTime(survey.submittedAt)}. You can change them
            {survey.closesOn ? ` until the end of ${formatDate(survey.closesOn)}` : ' until the survey closes'}.
          </Message>
        ) : null}

        <header className="tks-intro">
          {survey.description ? <p className="tks-intro__desc">{survey.description}</p> : null}
          <div className="tks-intro__meta">
            <span>
              <i className="bi bi-question-circle" aria-hidden="true" />
              {questionTotal} question{questionTotal === 1 ? '' : 's'}
            </span>
            {survey.closesOn ? (
              <span>
                <i className="bi bi-calendar-x" aria-hidden="true" />
                {closed ? 'Closed' : 'Open until the end of'} {formatDate(survey.closesOn)}
              </span>
            ) : null}
            {!closed ? (
              <span>
                <i className="bi bi-asterisk" aria-hidden="true" />
                Required questions are marked
              </span>
            ) : null}
          </div>
        </header>

        {anonymous && submitted ? null : (
          <SurveyForm surveyId={survey.id} content={survey.content} answers={answers} onChange={change} readOnly={closed} errors={errors} />
        )}

        {!closed && !(anonymous && submitted) ? (
          <footer className="tks-actions">
            <span className="tks-actions__note">
              {anonymous
                ? 'Kept in this browser until you send it'
                : dirty ? 'Unsaved changes' : survey.savedAt && !submitted ? `Saved ${formatDateTime(survey.savedAt)}` : ''}
            </span>
            {!submitted && !anonymous ? (
              <button type="button" className="bdc-btn" onClick={() => persist(false)} disabled={save.isPending || !dirty}>
                <i className="bi bi-floppy" aria-hidden="true" />
                Save for later
              </button>
            ) : null}
            <button type="button" className="bdc-btn bdc-btn--primary" onClick={send} disabled={save.isPending || (submitted && !dirty)}>
              <i className="bi bi-send" aria-hidden="true" />
              {save.isPending ? 'Sending…' : submitted ? 'Send changes' : 'Send answers'}
            </button>
          </footer>
        ) : (
          <footer className="tks-actions">
            <button type="button" className="bdc-btn" onClick={() => navigate('/surveys')}>
              <i className="bi bi-arrow-left" aria-hidden="true" />
              Back to surveys
            </button>
          </footer>
        )}
      </div>

      <Modal open={confirmSend} onClose={() => setConfirmSend(false)} size="xs">
        <Modal.Header>
          <Modal.Title>Send your answers?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {anonymous ? (
            "They are sent anonymously and can't be changed afterwards, because they aren't linked to you."
          ) : (
            <>
              You can still change them afterwards
              {survey.closesOn ? `, until the end of ${formatDate(survey.closesOn)}` : ', until the survey closes'}.
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setConfirmSend(false)} disabled={save.isPending}>Not yet</Button>
          <Button variant="primary" onClick={() => persist(true)} loading={save.isPending}>Send answers</Button>
        </Modal.Footer>
      </Modal>

      <Modal open={!!guard.pendingTo} onClose={guard.stay} size="xs">
        <Modal.Header>
          <Modal.Title>Leave without saving?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Your latest answers aren&rsquo;t saved. Leaving now loses them.</Modal.Body>
        <Modal.Footer>
          <Button onClick={guard.stay}>Stay here</Button>
          <Button variant="danger-solid" onClick={guard.leave}>Leave without saving</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
